import { produce } from 'immer';
import { NavIcons } from '@bbkAdminComponents/app-layout/nav';
import { type PivotT } from '@bbkAdminComponents/pivot-table/pivot-table';
import { type RestFunnel } from '@bbkAdminRedux/rtkq/funnels.slice';
import {
  type OfferPerfCtaPoint,
  type OfferPerfV2Point,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { type RuleMetadata } from '@bbkAdminRedux/rtkq/rules.slice';
import {
  type RestLossAversion,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import { NumberKit } from '@bbkAdminUtils/number-kit';
import { type Dictionary } from '@bbkAdminUtils/utility-types';

export const OFFER_PERF_TABLE_NAME = 'ChargebeeGrowth_Offer_Performance';

export const DEFAULT_OFFER_PERF_GROUP_BY = ['offer'];

export type OfferPerformanceRow = (OfferPerfV2Point | OfferPerfCtaPoint) & {
  ctaTotalViews?: number;
};

export type OfferPerformancePivotRowDefinition = {
  name: string;
  field: string[];
  label: string;
  icon?: React.ReactNode | null;
};

type CtaTextEntry = {
  cta_id: string;
  text?: string;
};

type OfferPerformanceCalculatedFieldsDeps = {
  rulesMap: Dictionary<RuleMetadata>;
  funnelsMap: Dictionary<RestFunnel>;
  offersAndLAsByIdMap: Dictionary<RestOffer | RestLossAversion>;
  ctasTextMap: Dictionary<CtaTextEntry>;
  selectedBrandLabel?: string;
  isLossAversion: (offer: RestOffer | RestLossAversion) => boolean;
  renderCategoryName: (category: RestOffer['category']) => string;
};

type OfferPerformanceReportTags = {
  funnelId: string;
  audience1Id: string;
  audience2Id?: string;
  audience3Id?: string;
  audience4Id?: string;
  offerId: string;
  ctaId?: string;
};

const OFFER_PERF_PIVOT_ROW_ICONS: Record<string, React.ReactNode> = {
  audience: NavIcons.audiences,
  experience: NavIcons.pages,
  placement: NavIcons.lossAversions,
  category: NavIcons.lossAversions,
  offer: NavIcons.offers2,
  cta: NavIcons.cta,
};

export const createOfferPerformanceCalculatedFields = ({
  rulesMap,
  funnelsMap,
  offersAndLAsByIdMap,
  ctasTextMap,
  selectedBrandLabel,
  isLossAversion,
  renderCategoryName,
}: OfferPerformanceCalculatedFieldsDeps) => {
  const getFunnelAudienceContent = (
    row: OfferPerformanceRow
  ): 'None' | string =>
    (row as OfferPerfCtaPoint).funnelAudContentCta ??
    (row as OfferPerfV2Point).funnelAudContent;

  const parseFunnelAudienceContent = (
    row: OfferPerformanceRow
  ): OfferPerformanceReportTags => {
    const isCtaPoint = Boolean((row as OfferPerfCtaPoint).funnelAudContentCta);
    const funnelAudContentCta = isCtaPoint
      ? (row as OfferPerfCtaPoint).funnelAudContentCta
      : (row as OfferPerfV2Point).funnelAudContent;

    const tags = funnelAudContentCta.split(',');

    const funnelIdIdx = 0;
    const funnelId = tags.at(funnelIdIdx)!;

    const offerIdIdx = isCtaPoint ? -2 : -1;
    const offerId = tags.at(offerIdIdx)!;

    const ctaIdIdx = isCtaPoint ? -1 : undefined;
    const ctaId = ctaIdIdx ? tags.at(ctaIdIdx) : undefined;

    const audience1IdIdx = 1;
    const audienceIds = tags.slice(audience1IdIdx, offerIdIdx);
    const audience1Id = audienceIds.shift()!;
    const audience2Id = audienceIds.shift();
    const audience3Id = audienceIds.shift();
    const audience4Id = audienceIds.shift();

    return {
      funnelId,
      audience1Id,
      audience2Id,
      audience3Id,
      audience4Id,
      offerId,
      ctaId,
    };
  };

  const getAudienceId = (
    audId: keyof OfferPerformanceReportTags,
    row: OfferPerformanceRow
  ) => {
    const reportTags = parseFunnelAudienceContent(row);
    return reportTags[audId];
  };

  const getAudienceDisplayName = (
    audId: keyof OfferPerformanceReportTags,
    row: OfferPerformanceRow
  ) => {
    const reportTags = parseFunnelAudienceContent(row);
    const audienceId = reportTags[audId];

    if (audienceId === 'control') return 'Control';
    if (audienceId === 'fallback') return 'Fallback';

    if (audienceId) {
      return rulesMap[audienceId]?.display_name ?? `Audience ${audienceId}`;
    }
  };

  return ((row) => ({
    funnelId: () => {
      const reportTags = parseFunnelAudienceContent(row);
      return parseInt(reportTags.funnelId, 10);
    },
    funnelDisplayName: () => {
      const funnelAudienceContent = getFunnelAudienceContent(row);
      if (funnelAudienceContent === 'None') return 'Unknown funnel';

      const reportTags = parseFunnelAudienceContent(row);
      const funnelId = reportTags.funnelId;

      if (funnelId) {
        return funnelsMap[funnelId]?.display_name ?? `Funnel ${funnelId}`;
      }

      return 'Unknown funnel';
    },
    audienceId: () => {
      return getAudienceId('audience1Id', row);
    },
    audienceDisplayName: () => {
      const funnelAudienceContent = getFunnelAudienceContent(row);
      if (funnelAudienceContent === 'None') return 'Fallback';

      const audienceDisplayName = getAudienceDisplayName('audience1Id', row);
      return audienceDisplayName ?? 'Unknown audience';
    },
    audience1Id: () => {
      return getAudienceId('audience1Id', row);
    },
    audience1DisplayName: () => {
      const funnelAudienceContent = getFunnelAudienceContent(row);
      if (funnelAudienceContent === 'None') return 'Fallback';

      const audienceDisplayName = getAudienceDisplayName('audience1Id', row);
      return audienceDisplayName ?? 'Unknown audience';
    },
    audience2Id: () => {
      return getAudienceId('audience2Id', row);
    },
    audience2DisplayName: () => {
      return getAudienceDisplayName('audience2Id', row);
    },
    audience3Id: () => {
      return getAudienceId('audience3Id', row);
    },
    audience3DisplayName: () => {
      return getAudienceDisplayName('audience3Id', row);
    },
    audience4Id: () => {
      return getAudienceId('audience4Id', row);
    },
    audience4DisplayName: () => {
      return getAudienceDisplayName('audience4Id', row);
    },
    offerId: () => {
      const reportTags = parseFunnelAudienceContent(row);
      return reportTags.offerId;
    },
    ctaId: () => {
      const reportTags = parseFunnelAudienceContent(row);
      return reportTags.ctaId;
    },
    ctaText: () => {
      const reportTags = parseFunnelAudienceContent(row);
      const ctaId = reportTags.ctaId;
      if (ctaId) {
        return ctasTextMap[ctaId]?.text ?? `CTA ${ctaId}`;
      }

      return 'Unknown';
    },
    brandName: () => {
      if (selectedBrandLabel) {
        return selectedBrandLabel;
      }

      return 'Unknown brand';
    },
    offerName: () => {
      const reportTags = parseFunnelAudienceContent(row);
      const contentId = reportTags.offerId;
      if (contentId) {
        return offersAndLAsByIdMap[contentId]?.display_name ?? 'Unknown offer';
      }
      return 'Unknown offer';
    },
    offerCategoryId: () => {
      const reportTags = parseFunnelAudienceContent(row);
      const contentId = reportTags.offerId;
      return offersAndLAsByIdMap[contentId]?.category;
    },
    offerCategory: () => {
      const reportTags = parseFunnelAudienceContent(row);
      const contentId = reportTags.offerId;
      if (contentId) {
        const content = offersAndLAsByIdMap[contentId];
        return content
          ? renderCategoryName(content.category)
          : 'Unknown category';
      }
      return 'Unknown category';
    },
    offerPlacement: () => {
      const reportTags = parseFunnelAudienceContent(row);
      const contentId = reportTags.offerId;
      if (contentId) {
        const offer = offersAndLAsByIdMap[contentId];
        if (offer && isLossAversion(offer)) {
          return 'Loss aversion';
        }
      }
      return 'Modal';
    },
    acceptRateCalc: () => {
      const totalViews =
        row.totalViews === 0 && isDefined(row.ctaTotalViews)
          ? row.ctaTotalViews
          : row.totalViews;
      return (row.acceptedOfferCount / totalViews) * 100;
    },
    saveRateCalc: () => (row.saveCount / row.convRateViewed) * 100,
    firstTimeRepurchaseRate: () =>
      (row.deflect_payment_sessions / row.acceptedOfferCount) * 100,
    lostAccepts: () => row.acceptedOfferCount - row.nonCanceledCount,
    watchlistAccepts: () => row.nonCanceledCount - row.saveCount,
  })) satisfies PivotT.CalculatedFields<OfferPerformanceRow>;
};

export const getOfferPerformancePivotRowDefinitions = (
  isNestedAudiencesEnabled: boolean
): OfferPerformancePivotRowDefinition[] => {
  const result: OfferPerformancePivotRowDefinition[] = [
    {
      field: ['audience1DisplayName'],
      icon: null,
      name: 'audience',
      label: 'Audiences',
    },
    {
      field: ['funnelDisplayName'],
      icon: null,
      name: 'experience',
      label: 'Experiences',
    },
    {
      field: ['offerPlacement'],
      icon: null,
      name: 'placement',
      label: 'Offer placement',
    },
    {
      field: ['offerCategory'],
      icon: null,
      name: 'category',
      label: 'Offer category',
    },
    {
      field: ['offerName'],
      icon: null,
      name: 'offer',
      label: 'Offer name',
    },
    {
      field: ['ctaText'],
      icon: null,
      name: 'cta',
      label: 'CTA Text',
    },
  ];

  if (isNestedAudiencesEnabled) {
    result[0]!.field = [
      'audience1DisplayName',
      'audience2DisplayName',
      'audience3DisplayName',
      'audience4DisplayName',
    ];
  }

  return result;
};

type OfferPerformancePivotRow = PivotT.ComputedFields<
  OfferPerformanceRow,
  ReturnType<typeof createOfferPerformanceCalculatedFields>
>;

type OfferPerformancePivotValues =
  PivotT.PivotValue<OfferPerformancePivotRow>[];

const genericOfferPerformancePivotValues: OfferPerformancePivotValues = [
  {
    label: 'Views',
    field: 'totalViews',
    format: NumberKit.formatNumber,
  },
  {
    label: 'Accepted offer',
    field: 'acceptedOfferCount',
    format: NumberKit.formatNumber,
  },
  {
    label: 'Offer Accept Rate',
    field: 'acceptRateCalc',
    format: NumberKit.formatPercent,
  },
  {
    label: 'Saves',
    field: 'saveCount',
    format: NumberKit.formatNumber,
  },
  {
    label: 'Lost Accepts',
    field: 'lostAccepts',
    format: NumberKit.formatNumber,
  },
  {
    label: 'Watchlist Accepts',
    field: 'watchlistAccepts',
    format: NumberKit.formatNumber,
  },
  {
    label: 'Offer conversion rate',
    field: 'saveRateCalc',
    format: NumberKit.formatPercent,
  },
];

const revenueOfferPerformancePivotValues: OfferPerformancePivotValues = [
  {
    label: 'Accepted offer (revenue)',
    field: 'acceptedOfferSavedRevenue',
    format: NumberKit.formatMoney,
  },
  {
    label: 'Saved revenue',
    field: 'saveCountSavedRevenue',
    format: NumberKit.formatMoney,
  },
];

const repurchaseOfferPerformancePivotValues: OfferPerformancePivotValues = [
  {
    label: 'First repurchase order',
    field: 'deflect_payment_sessions',
    format: NumberKit.formatNumber,
  },
  {
    label: 'First time repurchase rate',
    field: 'firstTimeRepurchaseRate',
    format: NumberKit.formatPercent,
  },
  {
    label: 'Cumulative retained revenue',
    field: 'deflect_payment_revenue',
    format: NumberKit.formatMoney,
  },
  {
    label: 'Total # of repurchase orders',
    field: 'deflect_payments_count',
    format: NumberKit.formatNumber,
  },
];

const ctaOfferPerformancePivotValues: OfferPerformancePivotValues = [
  {
    label: 'CTA Views',
    field: 'ctaTotalViews',
    format: NumberKit.formatNumber,
  },
];

export const getOfferPerformancePivotValues = ({
  isDemoData,
  mrrActive,
  isRepurchaseAttributionEnabled,
}: {
  isDemoData: boolean;
  mrrActive?: boolean;
  isRepurchaseAttributionEnabled?: boolean;
}): OfferPerformancePivotValues => {
  if (isDemoData) return genericOfferPerformancePivotValues;

  let updatedPivotValues = genericOfferPerformancePivotValues;
  if (mrrActive) {
    updatedPivotValues = produce(updatedPivotValues, (draft) => {
      draft.splice(2, 0, revenueOfferPerformancePivotValues[0]!);
      draft.splice(5, 0, revenueOfferPerformancePivotValues[1]!);
    });
  }
  if (isRepurchaseAttributionEnabled) {
    updatedPivotValues = produce(updatedPivotValues, (draft) => {
      draft.push(...repurchaseOfferPerformancePivotValues);
    });
  }
  updatedPivotValues = produce(updatedPivotValues, (draft) => {
    draft.splice(1, 0, ...ctaOfferPerformancePivotValues);
  });
  return updatedPivotValues;
};

const nonRevenueOfferPerformanceVisibleColumns = [
  'totalViews',
  'acceptedOfferCount',
  'acceptRateCalc',
  'saveCount',
  'saveRateCalc',
  'lostAccepts',
  'watchlistAccepts',
];

const revenueOfferPerformanceVisibleColumns = [
  'totalViews',
  'acceptedOfferCount',
  'acceptedOfferSavedRevenue',
  'acceptRateCalc',
  'saveCount',
  'saveCountSavedRevenue',
  'saveRateCalc',
  'lostAccepts',
  'watchlistAccepts',
];

export const getOfferPerformanceVisibleColumns = (
  mrrActive?: boolean,
  initVisibleColumns?: string[]
): string[] => {
  if (initVisibleColumns) return initVisibleColumns;
  return mrrActive
    ? revenueOfferPerformanceVisibleColumns
    : nonRevenueOfferPerformanceVisibleColumns;
};

export const resolveOfferPerformancePivotRows = (
  groupByOpts: string[],
  isNestedAudiencesEnabled: boolean
): PivotT.GroupFieldLoose<OfferPerformancePivotRow>[] =>
  groupByOpts
    .map((groupBy) =>
      getOfferPerformancePivotRowDefinitions(isNestedAudiencesEnabled).find(
        (row) => row.name === groupBy
      )
    )
    .filter(isDefined)
    .map((row) => ({
      ...row,
      icon: OFFER_PERF_PIVOT_ROW_ICONS[row.name] ?? null,
    }));
